import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "../../assets/generalTrade.css";

interface OfflineProduct {
  id: number;
  productName: string;
  SKU?: string | null;
  brand_name?: string | null;
}

const GeneralTradeHeader: React.FC = () => {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [products, setProducts] = useState<OfflineProduct[]>([]);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      try {
        const response = await axios.get(`${BASE}/general-trade/products`);
        const rows = response.data?.data || [];
        if (!cancelled) setProducts(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Unable to load General Trade products:", error);
        if (!cancelled) setProducts([]);
      }
    };

    void loadProducts();
    return () => {
      cancelled = true;
    };
  }, [BASE]);

  useEffect(() => {
    setProductsOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setProductsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, []);

  const closeMenus = () => {
    setProductsOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="gt-header">
      <div className="gt-topbar">
        <div className="gt-container gt-topbar-inner">
          <span>Bringing Quality Essentials Closer to Every Home</span>
          <div>
            <a href="mailto:generaltrade@lyrazone.com.my">
              generaltrade@lyrazone.com.my
            </a>
            <span className="gt-topbar-divider">|</span>
            <a href="tel:+60340501142">+60 3 4050 1142</a>
          </div>
        </div>
      </div>

      <nav className="gt-navbar">
        <div className="gt-container gt-navbar-inner">
          <Link to="/general-trade" className="gt-brand" onClick={closeMenus}>
            <img
              src="/images/general-trade/logo-website-header-general-trading.png"
              alt="Lyra Zone General Trading"
            />
          </Link>

          <button
            type="button"
            className="gt-mobile-toggle"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Toggle General Trade navigation"
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>

          <div className={`gt-nav-links ${mobileOpen ? "is-open" : ""}`}>
            <Link to="/general-trade" onClick={closeMenus}>
              Home
            </Link>
            <Link to="/general-trade/about" onClick={closeMenus}>
              About
            </Link>

            <div className="gt-products-menu" ref={menuRef}>
              <button
                type="button"
                className="gt-products-trigger"
                onClick={() => setProductsOpen((prev) => !prev)}
                aria-expanded={productsOpen}
              >
                Products <span className="gt-caret">▾</span>
              </button>

              {productsOpen && (
                <div className="gt-products-dropdown">
                  {products.length > 0 ? (
                    products.map((product) => (
                      <Link
                        key={product.id}
                        to={`/general-trade/product/${product.id}`}
                        onClick={closeMenus}
                      >
                        <strong>{product.productName}</strong>
                        {(product.brand_name || product.SKU) && (
                          <small>
                            {[product.brand_name, product.SKU]
                              .filter(Boolean)
                              .join(" • ")}
                          </small>
                        )}
                      </Link>
                    ))
                  ) : (
                    <span className="gt-products-empty">
                      No products available
                    </span>
                  )}
                </div>
              )}
            </div>

            <Link to="/general-trade/team" onClick={closeMenus}>
              Team
            </Link>
            <Link to="/general-trade/contact" onClick={closeMenus}>
              Contact Us
            </Link>
            <Link to="/" className="gt-division-switch" onClick={closeMenus}>
              Switch Division
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default GeneralTradeHeader;
